import React, { useEffect, useState } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Stack,
	Chip,
	Button,
	Box,
	CircularProgress,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import { useNotify } from 'react-admin';
import {
	listBetaRequests,
	listAllBetaRequests,
	decideBetaRequest,
	type ConnectorBetaRequest,
	type BetaRequestStatus,
} from '../../Services/betaRequests';
import BetaTesterModalCreate from './BetaTesterModalCreate';

interface BetaRequestsPanelProps {
	// sku fourni → demandes d'un seul connecteur (écran d'édition connecteur).
	// sku omis → vue globale toutes demandes, tous connecteurs (home admin),
	// avec une colonne "Connecteur" en plus.
	sku?: string;
}

const statusColor: Record<BetaRequestStatus, 'warning' | 'success' | 'error'> = {
	pending: 'warning',
	approved: 'success',
	rejected: 'error',
};

export const BetaRequestsPanel: React.FC<BetaRequestsPanelProps> = ({ sku }) => {
	const notify = useNotify();
	const isGlobal = !sku;
	const [filter, setFilter] = useState<BetaRequestStatus | 'all'>('pending');
	const [items, setItems] = useState<ConnectorBetaRequest[] | null>(null);
	const [decidingId, setDecidingId] = useState<number | null>(null);
	const [addOpen, setAddOpen] = useState(false);

	const load = async () => {
		setItems(null);
		try {
			const statusFilter = filter === 'all' ? undefined : (filter as BetaRequestStatus);
			const list = isGlobal
				? await listAllBetaRequests(statusFilter)
				: await listBetaRequests(sku as string, statusFilter);
			setItems(list);
		} catch (err: any) {
			notify(`Erreur lors du chargement: ${err?.message || 'unknown'}`, { type: 'error' });
			setItems([]);
		}
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sku, filter]);

	const handleDecide = async (id: number, status: 'approved' | 'rejected') => {
		// Au refus, on propose de saisir un motif (optionnel) renvoyé au demandeur
		// dans l'email Brevo. Annuler le prompt = refus sans motif (pas d'abandon).
		let reason: string | undefined;
		if (status === 'rejected') {
			reason = window.prompt('Motif du refus (optionnel, envoyé au demandeur) :') || undefined;
		}
		setDecidingId(id);
		try {
			await decideBetaRequest(id, status, reason);
			notify(`Demande ${status === 'approved' ? 'acceptée' : 'refusée'}`, { type: 'success' });
			await load();
		} catch (err: any) {
			notify(`Erreur: ${err?.message || 'unknown'}`, { type: 'error' });
		} finally {
			setDecidingId(null);
		}
	};

	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
					<Typography variant="h6">
						{isGlobal ? 'Demandes de test connecteurs' : 'Beta-testeurs'}
					</Typography>
					<Stack direction="row" alignItems="center" spacing={2}>
						<ToggleButtonGroup
							size="small"
							value={filter}
							exclusive
							onChange={(_, v) => v && setFilter(v)}
						>
							<ToggleButton value="pending">En attente</ToggleButton>
							<ToggleButton value="approved">Approuvées</ToggleButton>
							<ToggleButton value="rejected">Refusées</ToggleButton>
							<ToggleButton value="all">Toutes</ToggleButton>
						</ToggleButtonGroup>
						{!isGlobal && (
							<Button
								size="small"
								variant="contained"
								onClick={() => setAddOpen(true)}
							>
								Ajouter un beta-testeur
							</Button>
						)}
					</Stack>
				</Stack>

				{!isGlobal && (
					<BetaTesterModalCreate
						sku={sku as string}
						open={addOpen}
						handleClose={() => setAddOpen(false)}
						onAdded={load}
					/>
				)}

				{items === null && (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress size={28} />
					</Box>
				)}

				{items !== null && items.length === 0 && (
					<Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
						Aucune demande à afficher.
					</Typography>
				)}

				{items !== null && items.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow>
								{isGlobal && <TableCell>Connecteur</TableCell>}
								<TableCell>Utilisateur</TableCell>
								<TableCell>Projet</TableCell>
								<TableCell>Nb comptes</TableCell>
								<TableCell>Raison</TableCell>
								<TableCell>Date</TableCell>
								<TableCell>Statut</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((req) => (
								<TableRow key={req.id} hover>
									{isGlobal && (
										<TableCell>
											<Typography variant="body2" fontWeight={500}>
												{req.connectorName || req.sku}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{req.sku}
											</Typography>
										</TableCell>
									)}
									<TableCell>
										<Stack>
											<Typography variant="body2" fontWeight={500}>
												{req.userName || '—'}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{req.userEmail}
											</Typography>
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
											{req.projectFolderId || '—'}
										</Typography>
									</TableCell>
									<TableCell>{req.accountsToTest}</TableCell>
									<TableCell sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
										{req.reason}
									</TableCell>
									<TableCell>
										<Typography variant="caption">
											{new Date(req.createdAt).toLocaleDateString('fr-FR')}
										</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={req.status}
											size="small"
											color={statusColor[req.status]}
										/>
									</TableCell>
									<TableCell align="right">
										{req.status === 'pending' ? (
											<Stack direction="row" spacing={1} justifyContent="flex-end">
												<Button
													size="small"
													variant="contained"
													color="success"
													disabled={decidingId === req.id}
													onClick={() => handleDecide(req.id, 'approved')}
												>
													Accepter
												</Button>
												<Button
													size="small"
													variant="outlined"
													color="error"
													disabled={decidingId === req.id}
													onClick={() => handleDecide(req.id, 'rejected')}
												>
													Refuser
												</Button>
											</Stack>
										) : (
											<Stack>
												<Typography variant="caption" color="textSecondary">
													{req.decidedAt
														? `par ${req.decidedBy || '—'} le ${new Date(
																req.decidedAt
														  ).toLocaleDateString('fr-FR')}`
														: '—'}
												</Typography>
												{req.decisionReason && (
													<Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
														« {req.decisionReason} »
													</Typography>
												)}
											</Stack>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
};

export default BetaRequestsPanel;
